import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { counterpartyTypes } from '../../lib/counterpartyTypes'
import { markets } from '../../lib/markets'
import {
	validateCompanyNumber,
	getCompanyNumberLabel,
	getCompanyNumberPlaceholder,
	getCompanyNumberError,
} from '../../lib/companyNumberValidation'
import {
	validateEmail as validateEmailValue,
	validatePhone as validatePhoneValue,
} from '../../lib/contactValidation'
import { lookupOrganizationByCompanyNumber } from './contactEnrichmentUtils'

class ContactForm extends Component {
	constructor(props) {
		super(props)

		this.state = {
			emailErrors: {},
			phoneErrors: {},
			companyNumberError: null,
			companyNumberLookupLoading: false,
			companyNumberLookupResult: null,
		}

		this.companyNumberLookupTimer = null
	}

	componentWillUnmount() {
		if (this.companyNumberLookupTimer) {
			clearTimeout(this.companyNumberLookupTimer)
		}
	}

	componentDidMount() {
		// Trigger initial validation
		this.notifyValidationChange()
	}

	validateEmail = (index, email) => {
		const { emailErrors } = this.state
		const newEmailErrors = { ...emailErrors }

		const error = validateEmailValue(email)
		if (error) {
			newEmailErrors[index] = error
		} else {
			delete newEmailErrors[index]
		}

		this.setState({ emailErrors: newEmailErrors }, () => {
			this.notifyValidationChange()
		})
	}

	validatePhone = (index, phoneNumber, prefix) => {
		const { phoneErrors } = this.state
		const newPhoneErrors = { ...phoneErrors }

		if (!phoneNumber || phoneNumber.trim() === '') {
			delete newPhoneErrors[index]
			this.setState({ phoneErrors: newPhoneErrors }, () => {
				this.notifyValidationChange()
			})
			return
		}

		if (!prefix || prefix.trim() === '') {
			newPhoneErrors[index] = 'Prefix is required'
			this.setState({ phoneErrors: newPhoneErrors }, () => {
				this.notifyValidationChange()
			})
			return
		}

		const fullPhone = `${prefix.trim()}${phoneNumber.trim()}`
		const error = validatePhoneValue(fullPhone)
		if (error) {
			newPhoneErrors[index] = error
		} else {
			delete newPhoneErrors[index]
		}

		this.setState({ phoneErrors: newPhoneErrors }, () => {
			this.notifyValidationChange()
		})
	}

	validateCompanyNumber = (companyNumber, country) => {
		if (!companyNumber || companyNumber.trim() === '') {
			this.setState({ companyNumberError: null }, () => {
				this.notifyValidationChange()
			})
			return
		}

		const isValid = validateCompanyNumber(companyNumber, country)

		this.setState(
			{
				companyNumberError: isValid ? null : getCompanyNumberError(country),
			},
			() => {
				this.notifyValidationChange()
			},
		)
	}

	handleEmailChange = (index, value) => {
		this.props.onArrayFieldChange('emailAddresses', index, value)
		this.validateEmail(index, value)
	}

	handlePhoneChange = (index, field, value) => {
		this.props.onPhoneFieldChange(index, field, value)
		const { data } = this.props
		const phone = data.phoneNumbers[index]
		if (field === 'phoneNumber') {
			this.validatePhone(index, value, phone.prefix)
		} else if (field === 'prefix') {
			this.validatePhone(index, phone.phoneNumber, value)
		}
	}

	debouncedCompanyNumberLookup = (companyNumber) => {
		if (this.companyNumberLookupTimer) {
			clearTimeout(this.companyNumberLookupTimer)
		}

		if (!companyNumber || companyNumber.trim() === '') {
			this.setState({
				companyNumberLookupLoading: false,
				companyNumberLookupResult: null,
			}, () => this.notifyValidationChange())
			return
		}

		this.setState({
			companyNumberLookupLoading: true,
			companyNumberLookupResult: null,
		}, () => this.notifyValidationChange())

		this.companyNumberLookupTimer = setTimeout(async () => {
			try {
				const org = await lookupOrganizationByCompanyNumber(
					companyNumber.trim(),
				)
				this.setState({
					companyNumberLookupLoading: false,
					companyNumberLookupResult: org,
				}, () => this.notifyValidationChange())
			} catch (error) {
				console.warn('Company number lookup failed:', error)
				this.setState({
					companyNumberLookupLoading: false,
					companyNumberLookupResult: null,
				}, () => this.notifyValidationChange())
			}
		}, 500)
	}

	handleFieldChange = (field, value) => {
		this.props.onFieldChange(field, value)

		// Validate company number when country changes
		if (field === 'country') {
			const { data } = this.props
			this.validateCompanyNumber(data.companyNumber, value)
		} else if (field === 'companyNumber') {
			const { data } = this.props
			const isValid = validateCompanyNumber(value, data.country)
			this.validateCompanyNumber(value, data.country)
			if (isValid) {
				this.debouncedCompanyNumberLookup(value)
			} else {
				this.setState({
					companyNumberLookupLoading: false,
					companyNumberLookupResult: null,
				})
			}
		}

		// Notify validation change after field update (for required fields)
		setTimeout(() => this.notifyValidationChange(), 0)
	}

	handleRemoveEmail = (index) => {
		this.props.onRemoveArrayField('emailAddresses', index)
		// Clean up email errors - shift indices down
		const { emailErrors } = this.state
		const newEmailErrors = {}
		Object.keys(emailErrors).forEach((key) => {
			const idx = parseInt(key)
			if (idx < index) {
				newEmailErrors[idx] = emailErrors[idx]
			} else if (idx > index) {
				newEmailErrors[idx - 1] = emailErrors[idx]
			}
			// Skip the removed index
		})
		this.setState({ emailErrors: newEmailErrors }, () => {
			this.notifyValidationChange()
		})
	}

	handleRemovePhone = (index) => {
		this.props.onRemoveArrayField('phoneNumbers', index)
		// Clean up phone errors - shift indices down
		const { phoneErrors } = this.state
		const newPhoneErrors = {}
		Object.keys(phoneErrors).forEach((key) => {
			const idx = parseInt(key)
			if (idx < index) {
				newPhoneErrors[idx] = phoneErrors[idx]
			} else if (idx > index) {
				newPhoneErrors[idx - 1] = phoneErrors[idx]
			}
			// Skip the removed index
		})
		this.setState({ phoneErrors: newPhoneErrors }, () => {
			this.notifyValidationChange()
		})
	}

	hasValidationErrors = () => {
		const { emailErrors, phoneErrors, companyNumberError } = this.state
		return (
			Object.keys(emailErrors).length > 0 ||
			Object.keys(phoneErrors).length > 0 ||
			companyNumberError !== null
		)
	}

	// A lookup that turns up a different organization than the one this form
	// writes to means the number would end up on two organizations. Returns the
	// reason to show and to block saving with, or null when there is no clash.
	// Single source of truth for both the banner and isFormValid.
	getCompanyNumberConflictMessage = () => {
		const { data } = this.props
		const result = this.state.companyNumberLookupResult

		if (
			!result ||
			!data.organizationId ||
			result.organizationId === data.organizationId
		) {
			return null
		}

		return `organisation number already belongs to ${result.companyName}, which is not the organization being saved`
	}

	isFormValid = () => {
		const { data } = this.props

		// Helper function to check if a value is non-empty
		const hasValue = (val) => val && val.trim() !== ''

		// Check required fields
		const hasCounterPartyType = hasValue(data.counterpartyType)
		const hasCompanyName = hasValue(data.companyName)
		const hasCountry = hasValue(data.country)
		const hasRequiredFields =
			hasCounterPartyType && hasCompanyName && hasCountry

		// Check contact details - need at least one of: phone, full address, email, or company number
		const hasValidPhoneNumbers =
			data.phoneNumbers &&
			data.phoneNumbers.length > 0 &&
			data.phoneNumbers.some(
				(phone) => hasValue(phone.phoneNumber) && hasValue(phone.prefix),
			)
		const hasFullAddress =
			hasValue(data.address) && hasValue(data.postalCode) && hasValue(data.city)
		const hasEmail =
			data.emailAddresses &&
			data.emailAddresses.length > 0 &&
			data.emailAddresses.some(hasValue)
		const hasCompanyNumber = hasValue(data.companyNumber)
		const hasContactDetails =
			hasValidPhoneNumbers || hasFullAddress || hasEmail || hasCompanyNumber

		// Check for validation errors
		const hasErrors = this.hasValidationErrors()

		// Block save while looking up organization
		const isLookingUp = this.state.companyNumberLookupLoading

		// A company number belonging to another organization only blocks saving
		// where the caller offers a choice about which organization to write to
		// (the split modal). On the enrichment card there is nothing to choose —
		// it always updates the existing organization — so it is advisory there.
		const hasCompanyNumberConflict =
			!!this.props.blockOnCompanyNumberConflict &&
			!!this.getCompanyNumberConflictMessage()

		// Form is valid if all conditions are met
		return Boolean(
			hasRequiredFields &&
				hasContactDetails &&
				!hasErrors &&
				!isLookingUp &&
				!hasCompanyNumberConflict,
		)
	}

	notifyValidationChange = () => {
		const hasErrors = this.hasValidationErrors()
		const isValid = this.isFormValid()
		const conflictMessage = this.getCompanyNumberConflictMessage()

		this.lastNotified = { hasErrors, isValid, conflictMessage }

		if (this.props.onValidationChange) {
			this.props.onValidationChange(hasErrors)
		}
		if (this.props.onFormValidChange) {
			this.props.onFormValidChange(isValid, conflictMessage)
		}
	}

	// Validity also depends on props (the organization being saved changes when
	// the split modal's save mode changes), and those arrive without going
	// through a change handler. Re-notify only when the outcome actually moves,
	// otherwise the parent's setState would bounce straight back here.
	componentDidUpdate() {
		const last = this.lastNotified
		if (
			!last ||
			last.hasErrors !== this.hasValidationErrors() ||
			last.isValid !== this.isFormValid() ||
			last.conflictMessage !== this.getCompanyNumberConflictMessage()
		) {
			this.notifyValidationChange()
		}
	}

	getCallingCode = (countryCode) => {
		const market = markets.find((m) => m.code === countryCode)
		return market ? market.phoneNumberCode : ''
	}

	render() {
		const { data, readOnly } = this.props
		const { emailErrors, phoneErrors, companyNumberError } = this.state

		// Check which required fields are missing
		const isMissingCounterpartyType =
			!data.counterpartyType || data.counterpartyType.trim() === ''
		const isMissingCompanyName =
			!data.companyName || data.companyName.trim() === ''
		const isMissingCountry = !data.country || data.country.trim() === ''

		// A company number lookup can turn up the organization this form already
		// writes to, or a different one. Only the first is a link; the second is a
		// conflict, and reporting it as "linked" claims something untrue.
		const lookupResult = this.state.companyNumberLookupResult
		const companyNumberConflict = this.getCompanyNumberConflictMessage()
		const lookupIsCurrentOrganization =
			!!lookupResult && !!data.organizationId && !companyNumberConflict
		// Offer the link whenever the lookup turned up an organization that is not
		// the one being written to — whether this form has a current organization
		// or not. Without it a conflict can be reported with no way to act on it.
		const canLinkToLookupResult =
			!!lookupResult && !lookupIsCurrentOrganization && !readOnly

		return (
			<>
				{/* Row 1: Counter Party Type */}
				<div className='form-row-2'>
					<div className='form-field'>
						<label className='form-label'>
							Counter Party Type <span style={{ color: '#ef4444' }}>*</span>
						</label>
						<select
							className='form-select'
							value={data.counterpartyType || ''}
							onChange={(e) =>
								this.handleFieldChange('counterpartyType', e.target.value)
							}
							disabled={readOnly}
							style={{
								borderColor: isMissingCounterpartyType ? '#ef4444' : undefined,
							}}
						>
							<option value=''>— Select Counter Party Type —</option>
							{counterpartyTypes.map((type) => (
								<option key={type} value={type}>
									{type}
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Row 2: Company Number, Company Name */}
				<div className='form-row-2'>
					<div className='form-field'>
						<label className='form-label'>
							{getCompanyNumberLabel(data.country || 'DK')}
						</label>
						<input
							className='form-input'
							placeholder={getCompanyNumberPlaceholder(data.country || 'DK')}
							value={data.companyNumber || ''}
							onChange={(e) =>
								this.handleFieldChange('companyNumber', e.target.value)
							}
							disabled={readOnly}
							style={{
								borderColor: companyNumberError ? '#ef4444' : undefined,
							}}
						/>
						{companyNumberError && (
							<div
								style={{
									color: '#ef4444',
									fontSize: '12px',
									marginTop: '4px',
								}}
							>
								{companyNumberError}
							</div>
						)}
						{this.state.companyNumberLookupLoading && (
							<div
								style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}
							>
								Looking up organization...
							</div>
						)}
						{lookupIsCurrentOrganization && (
							<div
								style={{
									marginTop: '4px',
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									color: '#16a34a',
									fontSize: '12px',
									fontWeight: '600',
								}}
							>
								<svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
									<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
								</svg>
								This is the organization being saved: {lookupResult.companyName}
							</div>
						)}
						{companyNumberConflict && (
							<div
								style={{
									marginTop: '4px',
									// Red where it blocks saving, amber where it is advisory.
									color: this.props.blockOnCompanyNumberConflict
										? '#b91c1c'
										: '#92400e',
									fontSize: '12px',
									fontWeight: '600',
								}}
							>
								This {companyNumberConflict}.
							</div>
						)}
						{canLinkToLookupResult && (
							<div
								style={{
									marginTop: '4px',
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
								}}
							>
								<button
									type='button'
									onClick={() => {
										if (this.props.onOrganizationFound) {
											this.props.onOrganizationFound(lookupResult)
										}
									}}
									style={{
										background: '#16a34a',
										color: 'white',
										border: 'none',
										borderRadius: '4px',
										padding: '4px 8px',
										fontSize: '12px',
										fontWeight: '600',
										cursor: 'pointer',
									}}
								>
									Link to existing org: {lookupResult.companyName}
								</button>
							</div>
						)}
					</div>

					<div className='form-field'>
						<label className='form-label'>
							Company Name <span style={{ color: '#ef4444' }}>*</span>
						</label>
						<input
							className='form-input'
							placeholder='Enter company name'
							value={data.companyName || ''}
							maxLength={100}
							onChange={(e) =>
								this.handleFieldChange('companyName', e.target.value)
							}
							disabled={readOnly}
							style={{
								borderColor: isMissingCompanyName ? '#ef4444' : undefined,
							}}
						/>
					</div>
				</div>

				{/* Row 3: Address, ZIP Code, City, Country */}
				<div className='form-row-4'>
					<div className='form-field'>
						<label className='form-label'>Address</label>
						<input
							className='form-input'
							placeholder='Enter street address'
							value={data.address || ''}
							onChange={(e) =>
								this.handleFieldChange('address', e.target.value)
							}
							disabled={readOnly}
						/>
					</div>

					<div className='form-field'>
						<label className='form-label'>ZIP Code</label>
						<input
							className='form-input'
							placeholder='Enter ZIP code'
							value={data.postalCode || ''}
							onChange={(e) =>
								this.handleFieldChange('postalCode', e.target.value)
							}
							disabled={readOnly}
						/>
					</div>

					<div className='form-field'>
						<label className='form-label'>City</label>
						<input
							className='form-input'
							placeholder='Enter city'
							value={data.city || ''}
							onChange={(e) => this.handleFieldChange('city', e.target.value)}
							disabled={readOnly}
						/>
					</div>

					<div className='form-field'>
						<label className='form-label'>
							Country <span style={{ color: '#ef4444' }}>*</span>
						</label>
						<select
							className='form-select'
							value={data.country || ''}
							onChange={(e) =>
								this.handleFieldChange('country', e.target.value)
							}
							disabled={readOnly}
							style={{ borderColor: isMissingCountry ? '#ef4444' : undefined }}
						>
							<option value=''>— Select Country —</option>
							{markets.map((market) => (
								<option key={market.code} value={market.code}>
									{market.fullName}
								</option>
							))}
						</select>
					</div>
				</div>

				{/* Row 4: Email Addresses, Phone Numbers */}
				<div className='form-row-2'>
					<div className='form-field'>
						<label className='form-label'>Email Addresses</label>
						{(data.emailAddresses || []).map((email, idx) => (
							<div key={idx} style={{ marginBottom: '8px' }}>
								<div style={{ display: 'flex', gap: '8px' }}>
									<input
										type='email'
										className='form-input'
										placeholder='Enter email address'
										value={email}
										onChange={(e) =>
											this.handleEmailChange(idx, e.target.value)
										}
										disabled={readOnly}
										style={{
											flex: 1,
											borderColor: emailErrors[idx] ? '#ef4444' : undefined,
										}}
									/>
									{!readOnly && (
										<button
											type='button'
											className='save-button'
											onClick={() => this.handleRemoveEmail(idx)}
											style={{
												background: '#ef4444',
												flexShrink: 0,
											}}
										>
											Remove
										</button>
									)}
								</div>
								{emailErrors[idx] && (
									<div
										style={{
											color: '#ef4444',
											fontSize: '12px',
											marginTop: '4px',
										}}
									>
										{emailErrors[idx]}
									</div>
								)}
							</div>
						))}
						{(data.bouncedEmailAddresses || []).map((email) => (
							<div
								key={`bounced-${email}`}
								title='Emails to this address bounced'
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '8px',
									marginBottom: '8px',
									fontSize: '14px',
								}}
							>
								<span
									style={{
										textDecoration: 'line-through',
										color: '#9ca3af',
										overflowWrap: 'anywhere',
									}}
								>
									{email}
								</span>
								<span
									style={{
										color: '#ef4444',
										fontSize: '12px',
										flexShrink: 0,
									}}
								>
									Bounced
								</span>
							</div>
						))}
						{!readOnly && (
							<button
								type='button'
								className='save-button'
								onClick={() => this.props.onAddArrayField('emailAddresses')}
								style={{
									background: '#3b82f6',
									marginTop: '4px',
								}}
							>
								+ Add Email
							</button>
						)}
					</div>

					<div className='form-field'>
						<label className='form-label'>Phone Numbers</label>
						{(data.phoneNumbers || []).map((phone, idx) => (
							<div key={idx} style={{ marginBottom: '8px' }}>
								<div
									style={{
										display: 'flex',
										gap: '8px',
										alignItems: 'center',
									}}
								>
									<select
										className='form-select'
										value={phone.prefix || ''}
										onChange={(e) =>
											this.handlePhoneChange(idx, 'prefix', e.target.value)
										}
										disabled={readOnly}
										style={{ width: '120px', flexShrink: 0 }}
									>
										<option value=''>Select prefix</option>
										{markets.map((market) => (
											<option key={market.code} value={market.phoneNumberCode}>
												({market.phoneNumberCode}) {market.fullName}
											</option>
										))}
									</select>
									<input
										className='form-input'
										placeholder='Enter phone number'
										value={phone.phoneNumber || ''}
										onChange={(e) =>
											this.handlePhoneChange(idx, 'phoneNumber', e.target.value)
										}
										disabled={readOnly}
										style={{
											flex: 1,
											borderColor: phoneErrors[idx] ? '#ef4444' : undefined,
										}}
									/>
									{!readOnly && (
										<button
											type='button'
											className='save-button'
											onClick={() => this.handleRemovePhone(idx)}
											style={{
												background: '#ef4444',
												flexShrink: 0,
											}}
										>
											Remove
										</button>
									)}
								</div>
								{phoneErrors[idx] && (
									<div
										style={{
											color: '#ef4444',
											fontSize: '12px',
											marginTop: '4px',
										}}
									>
										{phoneErrors[idx]}
									</div>
								)}
							</div>
						))}
						{!readOnly && (
							<button
								type='button'
								className='save-button'
								onClick={() => this.props.onAddArrayField('phoneNumbers')}
								style={{
									background: '#3b82f6',
									marginTop: '4px',
								}}
							>
								+ Add Phone
							</button>
						)}
					</div>
				</div>
			</>
		)
	}
}

ContactForm.propTypes = {
	data: PropTypes.object.isRequired,
	readOnly: PropTypes.bool,
	// When true, a company number belonging to another organization blocks
	// saving instead of just warning. Set by callers that let the user choose
	// which organization is written to.
	blockOnCompanyNumberConflict: PropTypes.bool,
	onFieldChange: PropTypes.func.isRequired,
	onArrayFieldChange: PropTypes.func.isRequired,
	onAddArrayField: PropTypes.func.isRequired,
	onRemoveArrayField: PropTypes.func.isRequired,
	onPhoneFieldChange: PropTypes.func.isRequired,
	onValidationChange: PropTypes.func, // Callback for validation errors (email/phone)
	onFormValidChange: PropTypes.func, // Callback for overall form validity
	onOrganizationFound: PropTypes.func, // Callback when existing organization found by company number
}

export default ContactForm
